// One definition of the boat field rules, used by the main form's vuelidate
// tree and by the membership panels.
//
// The panels cannot mount a vuelidate tree of their own - there is one $v per
// component and the panel list is dynamic - so the rules are also exposed as
// plain predicates. Both come from the same constants, so a change to the
// accepted range cannot drift between the two places a dealer edits a boat.
import {
  required,
  requiredIf,
  maxLength,
  minLength,
  integer,
  between,
} from "vuelidate/lib/validators";
import { homePortHasStates } from "@/config/homePort";

export const MIN_BOAT_YEAR = 1900;
export const MIN_BOAT_LENGTH = 1;
export const MAX_BOAT_LENGTH = 65;

export function maxBoatYear() {
  return new Date().getFullYear() + 1;
}

// The vuelidate rules for $v.boats.
export const boatValidations = {
  color__c: {},
  engine_type__c: {},
  marina_name__c: {
    required,
  },
  year__c: {
    required,
    minLength: minLength(4),
    maxLength: maxLength(4),
    integer,
    between: between(MIN_BOAT_YEAR, maxBoatYear()),
  },
  length__c: {
    required,
    integer,
    maxLength: maxLength(2),
    between: between(MIN_BOAT_LENGTH, MAX_BOAT_LENGTH),
  },
  boat_make__c: {
    required,
  },
  name__c: {},
  registration_document__c: {},
  home_port_type__c: { required },
  home_port_city__c: { required },
  // Required only where a state exists to pick. Bahamas and Other have no
  // state list, and Salesforce holds a null state for every one of those boats
  // in production - so demanding one offered the dealer an empty dropdown and
  // an error they could not clear, and the renewal could not be submitted.
  home_port_state__c: {
    required: requiredIf(function (boats) {
      return homePortHasStates(boats.home_port_country__c);
    }),
  },
  home_port_country__c: { required },
};

function isWholeNumber(value) {
  return /^\d+$/.test(String(value).trim());
}

// Plain-predicate equivalents for the panels. Each returns an error message, or
// null when the value is acceptable, so the caller renders one consistent
// string rather than reimplementing the wording.
export function boatYearError(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "Year is required";
  }
  if (!isWholeNumber(value) || String(value).trim().length !== 4) {
    return "Year must be four digits";
  }
  const year = Number(value);
  const max = maxBoatYear();
  if (year < MIN_BOAT_YEAR || year > max) {
    return `Year must be between ${MIN_BOAT_YEAR} and ${max}`;
  }
  return null;
}

export function boatLengthError(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "Boat length is required";
  }
  if (!isWholeNumber(value)) {
    return "Boat length in whole feet only";
  }
  const length = Number(value);
  if (length < MIN_BOAT_LENGTH || length > MAX_BOAT_LENGTH) {
    return `Sea Tow generally accepts boats between ${MIN_BOAT_LENGTH}' and ${MAX_BOAT_LENGTH}'`;
  }
  return null;
}

export function boatMakeError(value) {
  if (!value || !String(value).trim()) {
    return "Boat make is required";
  }
  return null;
}
